import { useEffect, useState } from "react";
import { api } from "../lib/api";
import SinistreFloorPlan from "../components/SinistreFloorPlan";
import SinistreClaimForm from "../components/SinistreClaimForm";
import SinistreAdverseCard from "../components/SinistreAdverseCard";
import { toast } from "sonner";
import { ShieldAlert, Save, Plus, Trash2, Upload, Printer, FileText, Send } from "lucide-react";

const STATUS_COLORS = {
  "Brouillon": "bg-zinc-700 text-zinc-200",
  "Déclaré": "bg-blue-600 text-white",
  "En analyse": "bg-amber-600 text-white",
  "En attente de documents": "bg-orange-600 text-white",
  "Accepté": "bg-emerald-600 text-white",
  "Fermé": "bg-zinc-600 text-zinc-200",
};

const TABS = [
  ["resume", "Résumé"], ["timeline", "Chronologie"], ["tenants", "Propriété & locataires"],
  ["damages", "Dommages"], ["vendors", "Travaux / Fournisseurs"], ["documents", "Documents"],
  ["communications", "Communications assureur"], ["costs", "Coûts & factures"],
  ["next_actions", "Prochaines actions"], ["photos", "📸 Photos"],
  ["email_log", "📧 Journal des envois"], ["submission", "📄 Résumé assureur"], ["plan", "📐 Plan & mesures"], ["claimform", "📋 Claim experience"],
];

// Config des colonnes/champs par section liste.
const SECTIONS = {
  timeline: [["date", "Date", "date"], ["time", "Heure", "text"], ["type", "Type", "text"], ["note", "Description", "textarea"]],
  tenants: [["unit", "Logement", "text"], ["company", "Compagnie locataire", "text"], ["contact", "Contact", "text"], ["phone", "Téléphone", "text"], ["email", "Courriel", "text"], ["insurance", "Assurance", "text"]],
  damages: [["unit", "Logement", "text"], ["room", "Pièce", "text"], ["damage_type", "Type de dommage", "text"], ["water_active", "Eau active", "select:Oui,Non,Inconnu"], ["mold", "Moisissure", "select:Oui,Non,À confirmer"], ["materials", "Matériaux touchés", "text"], ["urgency", "Urgence", "select:Faible,Modérée,Élevée"], ["notes", "Notes", "textarea"]],
  vendors: [["name", "Fournisseur", "text"], ["date", "Date", "date"], ["work", "Travaux", "text"], ["equipment", "Équipement", "text"], ["contract_signed", "Contrat signé", "bool"]],
  communications: [["date", "Date", "date"], ["time", "Heure", "text"], ["insurer", "Compagnie", "text"], ["person", "Contact", "text"], ["method", "Moyen", "select:Téléphone,Courriel,Portail"], ["summary", "Résumé", "textarea"], ["instructions", "Instructions reçues", "textarea"], ["followup", "Suivi", "date"]],
  costs: [["vendor", "Fournisseur", "text"], ["invoice_no", "N° facture", "text"], ["category", "Catégorie", "text"], ["amount_before", "Avant taxes", "number"], ["tax", "Taxes", "number"], ["total", "Total", "number"], ["billed_to", "Facturé à", "select:Propriétaire,Assureur,Locataire,À confirmer"], ["paid", "Payé", "select:Oui,Non,Partiel"], ["reimbursable", "Remboursable", "select:Oui,Non,À confirmer"]],
  next_actions: [["task", "Tâche", "text"], ["owner", "Responsable", "select:Propriétaire,Assureur,Locataire,Fournisseur"], ["due_date", "Échéance", "date"], ["priority", "Priorité", "select:Basse,Normale,Haute"], ["done", "Fait", "bool"]],
};

const inp = "bg-zinc-950 border border-zinc-700 rounded px-2 py-1.5 text-sm text-zinc-100 w-full";

function FieldInput({ def, value, onChange }) {
  const [key, label, type] = def;
  if (type?.startsWith("select:")) {
    const opts = type.split(":")[1].split(",");
    return <select className={inp} value={value || ""} onChange={(e) => onChange(key, e.target.value)} data-testid={`f-${key}`}>
      <option value="">—</option>{opts.map((o) => <option key={o} value={o}>{o}</option>)}
    </select>;
  }
  if (type === "textarea") return <textarea className={inp} rows={2} value={value || ""} onChange={(e) => onChange(key, e.target.value)} placeholder={label} data-testid={`f-${key}`} />;
  if (type === "bool") return <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!value} onChange={(e) => onChange(key, e.target.checked)} data-testid={`f-${key}`} /> {label}</label>;
  return <input type={type === "number" ? "number" : type === "date" ? "date" : "text"} className={inp} value={value || ""} onChange={(e) => onChange(key, e.target.value)} placeholder={label} data-testid={`f-${key}`} />;
}

export default function DossierSinistreModule() {
  const [claim, setClaim] = useState(null);
  const [statuses, setStatuses] = useState([]);
  const [tab, setTab] = useState("resume");
  const [form, setForm] = useState({});
  const [newItem, setNewItem] = useState({});
  const [saving, setSaving] = useState(false);
  const [insurerEmail, setInsurerEmail] = useState("");
  const [tenantEmail, setTenantEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [sendingTenant, setSendingTenant] = useState(false);
  // Compositeur de courriel personnalisé (M. Laroche / autre)
  const [letterTo, setLetterTo] = useState("");
  const [letterCc, setLetterCc] = useState("ralph646@hotmail.com");
  const [letterSubject, setLetterSubject] = useState("");
  const [letterBody, setLetterBody] = useState("");
  const [letterPdf, setLetterPdf] = useState(false);
  const [letterPhotos, setLetterPhotos] = useState(false);
  const [letterFollowup, setLetterFollowup] = useState(true);
  const [drafting, setDrafting] = useState(false);
  const [sendingCustom, setSendingCustom] = useState(false);
  const [photoRoom, setPhotoRoom] = useState("");
  const [photoPhase, setPhotoPhase] = useState("Avant");
  const [emailLog, setEmailLog] = useState(null);

  const load = async () => {
    const { data } = await api.get("/dossier-sinistre");
    setStatuses(data.statuses || []);
    const c = data.claims?.[0];
    if (c) { setClaim(c); setForm(c); }
  };
  useEffect(() => { load().catch(() => toast.error("Erreur de chargement")); }, []);

  const saveSummary = async () => {
    setSaving(true);
    try {
      const fields = ["title", "property_address", "sinistre_type", "discovered_at", "cause", "severity", "insurer", "policy_number", "claim_number", "expert_name", "expert_contact", "liability_insurer", "liability_adjuster", "liability_adjuster_contact", "liability_claim_number", "coverage_limit", "status", "owner_name", "owner_contact", "building_type", "deductible", "coverage_notes"];
      const patch = {}; fields.forEach((f) => patch[f] = form[f]);
      patch.units = (typeof form.units === "string" ? form.units.split(",").map((s) => s.trim()).filter(Boolean) : form.units) || [];
      const { data } = await api.put(`/dossier-sinistre/${claim.id}`, patch);
      setClaim(data); setForm(data); toast.success("Dossier enregistré ✓");
    } catch (e) { toast.error("Erreur d'enregistrement"); } finally { setSaving(false); }
  };

  const addItem = async (section) => {
    try {
      await api.post(`/dossier-sinistre/${claim.id}/items/${section}`, newItem);
      setNewItem({}); await load(); toast.success("Ajouté ✓");
    } catch (e) { toast.error("Erreur ajout"); }
  };
  const delItem = async (section, id) => {
    try { await api.delete(`/dossier-sinistre/${claim.id}/items/${section}/${id}`); await load(); }
    catch (e) { toast.error("Erreur suppression"); }
  };
  const toggleItem = async (section, id, key, val) => {
    try { await api.put(`/dossier-sinistre/${claim.id}/items/${section}/${id}`, { [key]: val }); await load(); }
    catch (e) { toast.error("Erreur"); }
  };
  const uploadDoc = async (file, category) => {
    const fd = new FormData(); fd.append("file", file);
    try {
      await api.post(`/dossier-sinistre/${claim.id}/upload?category=${encodeURIComponent(category || "Autre")}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      await load(); toast.success("Document ajouté ✓");
    } catch (e) { toast.error("Erreur d'upload"); }
  };

  const sendToInsurer = async () => {
    if (!insurerEmail.trim()) { toast.error("Entrez l'adresse courriel de Desjardins"); return; }
    setSending(true);
    try {
      const { data } = await api.post(`/dossier-sinistre/${claim.id}/send-to-insurer`, {
        to_email: insurerEmail.trim(),
        cc: "ralph646@hotmail.com",
      });
      toast.success(`Envoyé à ${data.to} · ${data.attachments} pièce(s) jointe(s) ✓`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur d'envoi du courriel");
    } finally { setSending(false); }
  };

  const notifyTenant = async () => {
    if (!tenantEmail.trim()) { toast.error("Entrez l'adresse courriel de l'organisme locataire"); return; }
    setSendingTenant(true);
    try {
      const { data } = await api.post(`/dossier-sinistre/${claim.id}/notify-tenant`, {
        to_email: tenantEmail.trim(),
        cc: "ralph646@hotmail.com",
      });
      toast.success(`Message envoyé à ${data.to} ✓`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur d'envoi du courriel");
    } finally { setSendingTenant(false); }
  };

  const generateLaroche = async () => {
    setDrafting(true);
    try {
      const { data } = await api.post(`/dossier-sinistre/${claim.id}/draft-liability-letter`, { type: "laroche" });
      setLetterSubject(data.subject || "");
      setLetterBody(data.body_text || "");
      if (data.to_email) setLetterTo(data.to_email);
      setLetterFollowup(true);
      toast.success("Brouillon M. Laroche généré ✓ — relis-le puis clique Envoyer");
    } catch (e) { toast.error("Erreur de génération du brouillon"); }
    finally { setDrafting(false); }
  };

  const generatePreservation = async () => {
    setDrafting(true);
    try {
      const { data } = await api.post(`/dossier-sinistre/${claim.id}/draft-liability-letter`, { type: "preservation" });
      setLetterSubject(data.subject || "");
      setLetterBody(data.body_text || "");
      setLetterTo("");
      setLetterFollowup(false);
      toast.success("Brouillon préservation des preuves généré ✓ — ajoute le destinataire (Mino / Câlinet)");
    } catch (e) { toast.error("Erreur de génération du brouillon"); }
    finally { setDrafting(false); }
  };

  const markResponded = async () => {
    try {
      await api.post(`/dossier-sinistre/${claim.id}/followup/mark-responded`);
      toast.success("Relance marquée comme répondue ✓");
      await load();
    } catch (e) { toast.error("Erreur"); }
  };

  const loadEmailLog = async () => {
    try {
      const { data } = await api.get(`/dossier-sinistre/email-log`);
      setEmailLog(data.emails || []);
    } catch (e) { setEmailLog([]); }
  };

  const uploadPhoto = async (file) => {
    const qs = new URLSearchParams({ room: photoRoom || "Général", phase: photoPhase || "Avant" }).toString();
    const fd = new FormData(); fd.append("file", file);
    try {
      await api.post(`/dossier-sinistre/${claim.id}/upload-photo?${qs}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      await load(); toast.success("Photo ajoutée ✓");
    } catch (e) { toast.error("Erreur d'upload de la photo"); }
  };

  const savePhotoNote = async (photoId, note) => {
    try {
      await api.patch(`/dossier-sinistre/${claim.id}/photo/${photoId}`, { note });
      toast.success("Légende enregistrée ✓");
      await load();
    } catch (e) { toast.error("Erreur d'enregistrement de la légende"); }
  };

  const sendCustom = async () => {
    if (!letterTo.trim()) { toast.error("Entrez l'adresse du destinataire"); return; }
    if (!letterBody.trim()) { toast.error("Le courriel est vide — génère ou écris le brouillon"); return; }
    if (!window.confirm(`Envoyer ce courriel à ${letterTo.trim()} ?\n\nObjet : ${letterSubject}\n\n(Une copie sera envoyée à ${letterCc || "personne"}.)`)) return;
    setSendingCustom(true);
    try {
      const { data } = await api.post(`/dossier-sinistre/${claim.id}/send-custom`, {
        to_email: letterTo.trim(), cc: letterCc.trim() || undefined,
        subject: letterSubject, body_text: letterBody,
        include_pdf: letterPdf, include_photos: letterPhotos, set_followup: letterFollowup,
        recipient_label: claim.liability_insurer || "Assureur responsabilité",
      });
      toast.success(`Envoyé à ${data.to} · ${data.attachments} pièce(s) jointe(s) ✓`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur d'envoi du courriel");
    } finally { setSendingCustom(false); }
  };

  const downloadPdf = async () => {
    try {
      const res = await api.get(`/dossier-sinistre/${claim.id}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `dossier-sinistre-${claim.policy_number || claim.id}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("PDF généré ✓");
    } catch (e) { toast.error("Erreur de génération du PDF"); }
  };

  if (!claim) return <div className="p-6 text-zinc-400">Chargement du dossier…</div>;

  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setNI = (k, v) => setNewItem((n) => ({ ...n, [k]: v }));

  return (
    <div className="p-6 text-zinc-100 max-w-6xl mx-auto" data-testid="dossier-sinistre-module">
      <div className="flex items-start justify-between gap-4 mb-2">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2"><ShieldAlert className="text-amber-400" /> Dossier de sinistre</h1>
          <p className="text-zinc-400 text-sm">{claim.title} · {claim.property_address}</p>
        </div>
        <span className={`px-3 py-1.5 rounded-full text-sm font-semibold ${STATUS_COLORS[claim.status] || "bg-zinc-700"}`} data-testid="claim-status">{claim.status}</span>
      </div>

      {claim.laroche_followup && !claim.laroche_followup.responded && (
        <div className="mb-4 rounded-xl border border-orange-700/60 bg-orange-950/30 p-3 flex items-center justify-between gap-3 flex-wrap" data-testid="followup-banner">
          <div className="text-sm text-orange-200">
            ⏰ <b>Relance programmée</b> — courriel envoyé à {claim.laroche_followup.to_email} le {(claim.laroche_followup.sent_at || "").slice(0, 10)}.
            {" "}Rappel prévu si pas de réponse d'ici le <b>{(claim.laroche_followup.due_at || "").slice(0, 10)}</b>.
            {claim.laroche_followup.reminded_at && <span className="text-orange-400"> · Rappel déjà envoyé ✓</span>}
          </div>
          <button onClick={markResponded} data-testid="mark-responded-btn"
            className="bg-emerald-600 text-white text-xs font-semibold px-3 py-1.5 rounded-lg hover:bg-emerald-500 whitespace-nowrap">✓ Marquer comme répondu</button>
        </div>
      )}

      <div className="flex flex-wrap gap-1 border-b border-zinc-800 mb-5 mt-4">
        {TABS.map(([k, l]) => (
          <button key={k} onClick={() => { setTab(k); setNewItem({}); if (k === "email_log") loadEmailLog(); }} data-testid={`tab-${k}`}
            className={`px-3 py-2 text-sm font-semibold border-b-2 ${tab === k ? "border-amber-500 text-amber-400" : "border-transparent text-zinc-400 hover:text-zinc-200"}`}>{l}</button>
        ))}
      </div>

      {/* RÉSUMÉ */}
      {tab === "resume" && (
        <div className="space-y-4" data-testid="tab-content-resume">
          {claim && <SinistreAdverseCard key={claim.id} claim={claim} onSaved={load} />}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {[["title", "Titre du sinistre"], ["property_address", "Adresse du bien"], ["sinistre_type", "Type de sinistre"], ["discovered_at", "Date de découverte", "date"], ["cause", "Cause supposée", "textarea"], ["severity", "Gravité", "select:mineur,modéré,majeur"], ["insurer", "Assureur (le vôtre)"], ["policy_number", "N° de police"], ["claim_number", "N° de dossier réclamation"], ["expert_name", "Expert en sinistre"], ["expert_contact", "Coordonnées de l'expert"], ["liability_insurer", "Assureur responsabilité (locataire)"], ["liability_adjuster", "Expert responsabilité (ex. M. Laroche)"], ["liability_adjuster_contact", "Coordonnées expert responsabilité"], ["liability_claim_number", "N° dossier responsabilité"], ["coverage_limit", "Limite de couverture ($)", "number"], ["owner_name", "Nom du propriétaire"], ["owner_contact", "Coordonnées propriétaire"], ["building_type", "Type d'immeuble"], ["deductible", "Franchise"], ["coverage_notes", "Notes de couverture", "textarea"]].map((d) => (
              <div key={d[0]}><label className="text-xs text-zinc-400">{d[1]}</label><FieldInput def={[d[0], d[1], d[2] || "text"]} value={form[d[0]]} onChange={setF} /></div>
            ))}
            <div><label className="text-xs text-zinc-400">Logements touchés (séparés par virgule)</label>
              <input className={inp} value={Array.isArray(form.units) ? form.units.join(", ") : (form.units || "")} onChange={(e) => setF("units", e.target.value)} data-testid="f-units" /></div>
            <div><label className="text-xs text-zinc-400">Statut</label>
              <select className={inp} value={form.status || ""} onChange={(e) => setF("status", e.target.value)} data-testid="f-status">
                {statuses.map((s) => <option key={s} value={s}>{s}</option>)}</select></div>
          </div>
          <button onClick={saveSummary} disabled={saving} data-testid="save-summary-btn"
            className="flex items-center gap-2 bg-amber-500 text-zinc-950 font-semibold px-5 py-2.5 rounded-lg hover:bg-amber-400 disabled:opacity-50">
            <Save size={16} /> {saving ? "Enregistrement…" : "Enregistrer le dossier"}</button>
        </div>
      )}

      {/* SECTIONS LISTES */}
      {SECTIONS[tab] && (
        <div className="space-y-4" data-testid={`tab-content-${tab}`}>
          {tab === "costs" && (() => {
            const costs = claim.costs || [];
            const sum = (key) => costs.reduce((a, c) => a + (parseFloat(c[key]) || 0), 0);
            const totBefore = sum("amount_before"), totTax = sum("tax"), totTotal = sum("total");
            const coverage = parseFloat(claim.coverage_limit) || 0;
            const franchise = parseFloat(String(claim.deductible || "").replace(/[^\d.]/g, "")) || 0;
            const resteACharge = Math.max(0, totTotal - coverage) + (coverage > 0 ? franchise : 0);
            const fmtMoney = (n) => n.toLocaleString("fr-CA", { style: "currency", currency: "CAD" });
            return (
              <div className="rounded-xl border border-emerald-800/60 bg-emerald-950/20 p-4 space-y-3" data-testid="costs-total">
                <div className="flex flex-wrap items-center gap-x-8 gap-y-2">
                  <div><div className="text-xs text-zinc-400">Total avant taxes</div><div className="text-lg font-bold text-zinc-100">{fmtMoney(totBefore)}</div></div>
                  <div><div className="text-xs text-zinc-400">Taxes</div><div className="text-lg font-bold text-zinc-100">{fmtMoney(totTax)}</div></div>
                  <div><div className="text-xs text-emerald-400">TOTAL ({costs.length} facture{costs.length > 1 ? "s" : ""})</div><div className="text-2xl font-bold text-emerald-400" data-testid="costs-grand-total">{fmtMoney(totTotal)}</div></div>
                </div>
                {coverage > 0 && (
                  <div className="flex flex-wrap items-center gap-x-8 gap-y-2 border-t border-emerald-900/50 pt-3" data-testid="coverage-recap">
                    <div><div className="text-xs text-zinc-400">Couverture assurance</div><div className="text-lg font-bold text-sky-300">{fmtMoney(coverage)}</div></div>
                    {franchise > 0 && <div><div className="text-xs text-zinc-400">Franchise</div><div className="text-lg font-bold text-zinc-300">{fmtMoney(franchise)}</div></div>}
                    <div><div className="text-xs text-orange-400">Reste à charge estimé</div><div className="text-2xl font-bold text-orange-400" data-testid="reste-a-charge">{fmtMoney(resteACharge)}</div></div>
                    <div className="text-[11px] text-zinc-500 max-w-xs">{totTotal <= coverage ? "✓ Le total est couvert (hors franchise)." : `⚠ Le total dépasse la couverture de ${fmtMoney(totTotal - coverage)}.`}</div>
                  </div>
                )}
              </div>
            );
          })()}
          <div className="rounded-xl border border-zinc-800 p-4 bg-zinc-900/40">
            <div className="text-sm font-semibold mb-3 text-amber-300">Ajouter</div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
              {SECTIONS[tab].map((def) => (
                <div key={def[0]}><label className="text-xs text-zinc-400">{def[1]}</label>
                  <FieldInput def={def} value={newItem[def[0]]} onChange={setNI} /></div>
              ))}
            </div>
            <button onClick={() => addItem(tab)} data-testid={`add-${tab}-btn`}
              className="mt-3 flex items-center gap-2 bg-zinc-800 border border-amber-700 text-amber-300 px-4 py-2 rounded-lg hover:bg-zinc-700 text-sm"><Plus size={15} /> Ajouter</button>
          </div>

          <div className="space-y-2">
            {(claim[tab] || []).length === 0 && <p className="text-zinc-500 text-sm">Aucune entrée pour le moment.</p>}
            {(claim[tab] || []).map((it) => (
              <div key={it.id} className="rounded-lg border border-zinc-800 p-3 flex items-start justify-between gap-3" data-testid={`item-${tab}-${it.id}`}>
                <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm">
                  {SECTIONS[tab].map((def) => {
                    const v = it[def[0]];
                    if (def[2] === "bool") return <label key={def[0]} className="flex items-center gap-1 text-zinc-300">
                      <input type="checkbox" checked={!!v} onChange={(e) => toggleItem(tab, it.id, def[0], e.target.checked)} /> {def[1]}</label>;
                    return <span key={def[0]} className="text-zinc-300"><span className="text-zinc-500">{def[1]}:</span> {v || "—"}</span>;
                  })}
                </div>
                <button onClick={() => delItem(tab, it.id)} data-testid={`del-${tab}-${it.id}`} className="text-zinc-500 hover:text-red-400 shrink-0"><Trash2 size={16} /></button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* DOCUMENTS */}
      {tab === "documents" && (
        <div className="space-y-4" data-testid="tab-content-documents">
          <div className="rounded-xl border border-zinc-800 p-4 bg-zinc-900/40 flex items-center gap-3">
            <label className="flex items-center gap-2 bg-amber-500 text-zinc-950 font-semibold px-4 py-2 rounded-lg cursor-pointer hover:bg-amber-400" data-testid="upload-doc-label">
              <Upload size={16} /> Téléverser un document
              <input type="file" className="hidden" data-testid="upload-doc-input"
                onChange={(e) => e.target.files[0] && uploadDoc(e.target.files[0], "Autre")} />
            </label>
            <span className="text-xs text-zinc-500">Photos, PDF, contrats, factures — stockage permanent.</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {(claim.documents || []).length === 0 && <p className="text-zinc-500 text-sm">Aucun document.</p>}
            {(claim.documents || []).map((d) => (
              <div key={d.id} className="rounded-lg border border-zinc-800 p-3 flex items-center justify-between gap-3" data-testid={`doc-${d.id}`}>
                <a href={d.url} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-amber-300 hover:underline truncate">
                  <FileText size={16} /> {d.filename || d.url}</a>
                <div className="flex items-center gap-2 shrink-0">
                  <span className="text-xs text-zinc-500">{d.category}</span>
                  <button onClick={() => delItem("documents", d.id)} data-testid={`del-doc-${d.id}`} className="text-zinc-500 hover:text-red-400"><Trash2 size={15} /></button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* PHOTOS horodatées (avant / après, par pièce) */}
      {tab === "plan" && claim && <SinistreFloorPlan key={claim.id} claim={claim} onSaved={load} />}
      {tab === "claimform" && claim && <SinistreClaimForm key={claim.id} claim={claim} onSaved={load} />}

      {tab === "photos" && (
        <div className="space-y-4" data-testid="tab-content-photos">
          <div className="rounded-xl border border-zinc-800 p-4 bg-zinc-900/40 space-y-3">
            <div className="text-sm font-semibold text-amber-300">Ajouter une photo</div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              <div><label className="text-xs text-zinc-400">Pièce</label>
                <input className={inp} value={photoRoom} onChange={(e) => setPhotoRoom(e.target.value)} placeholder="Ex : Salon sous-sol, Chambre étage…" data-testid="photo-room-input" /></div>
              <div><label className="text-xs text-zinc-400">Phase</label>
                <select className={inp} value={photoPhase} onChange={(e) => setPhotoPhase(e.target.value)} data-testid="photo-phase-select">
                  <option>Avant</option><option>Pendant</option><option>Après</option></select></div>
              <div className="flex items-end">
                <label className="flex items-center gap-2 bg-amber-500 text-zinc-950 font-semibold px-4 py-2 rounded-lg cursor-pointer hover:bg-amber-400 w-full justify-center" data-testid="upload-photo-label">
                  <Upload size={16} /> Téléverser
                  <input type="file" accept="image/*" className="hidden" data-testid="upload-photo-input"
                    onChange={(e) => e.target.files[0] && uploadPhoto(e.target.files[0])} /></label>
              </div>
            </div>
            <p className="text-xs text-zinc-500">Chaque photo est horodatée automatiquement et rattachée à la pièce + la phase, pour appuyer ta réclamation.</p>
          </div>
          {(() => {
            const photos = claim.photos || [];
            if (photos.length === 0) return <p className="text-zinc-500 text-sm">Aucune photo pour le moment.</p>;
            const rooms = [...new Set(photos.map((p) => p.room || "Général"))];
            return rooms.map((room) => (
              <div key={room} className="space-y-2" data-testid={`photo-room-${room}`}>
                <div className="text-sm font-semibold text-zinc-200 border-b border-zinc-800 pb-1">{room}</div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {photos.filter((p) => (p.room || "Général") === room).map((p) => (
                    <div key={p.id} className="rounded-lg border border-zinc-800 overflow-hidden bg-zinc-900/40" data-testid={`photo-${p.id}`}>
                      <a href={p.url} target="_blank" rel="noreferrer"><img src={p.url} alt={p.note || room} className="w-full h-28 object-cover" /></a>
                      <div className="p-2 flex items-center justify-between gap-1">
                        <div className="text-[10px] text-zinc-400 leading-tight">
                          <span className={`px-1.5 py-0.5 rounded ${p.phase === "Après" ? "bg-emerald-600/30 text-emerald-300" : p.phase === "Pendant" ? "bg-amber-600/30 text-amber-300" : "bg-sky-600/30 text-sky-300"}`}>{p.phase}</span>
                          <div className="mt-0.5">{(p.taken_at || "").slice(0, 10)}</div>
                        </div>
                        <button onClick={() => delItem("photos", p.id)} data-testid={`del-photo-${p.id}`} className="text-zinc-500 hover:text-red-400"><Trash2 size={14} /></button>
                      </div>
                      <input type="text" defaultValue={p.note || ""} placeholder="Légende (ex. membrane retirée)…"
                        onBlur={(e) => { if ((e.target.value || "") !== (p.note || "")) savePhotoNote(p.id, e.target.value); }}
                        data-testid={`photo-note-${p.id}`}
                        className="w-full text-[11px] bg-zinc-950 border-t border-zinc-800 px-2 py-1.5 text-zinc-200 focus:outline-none focus:bg-zinc-900" />
                    </div>
                  ))}
                </div>
              </div>
            ));
          })()}
        </div>
      )}

      {/* JOURNAL DES ENVOIS */}
      {tab === "email_log" && (
        <div className="space-y-3" data-testid="tab-content-email_log">
          <div className="flex items-center justify-between">
            <p className="text-sm text-zinc-400">Historique de tous les courriels envoyés depuis ce dossier.</p>
            <button onClick={loadEmailLog} data-testid="reload-email-log-btn" className="text-xs text-amber-300 underline">Rafraîchir</button>
          </div>
          {emailLog === null ? <p className="text-zinc-500 text-sm">Chargement…</p>
            : emailLog.length === 0 ? <p className="text-zinc-500 text-sm">Aucun courriel envoyé pour le moment.</p>
            : (
              <div className="overflow-x-auto rounded-lg border border-zinc-800">
                <table className="w-full text-xs">
                  <thead><tr className="text-zinc-400 text-left bg-zinc-900/60">
                    <th className="py-2 px-2">Date</th><th className="py-2 px-2">À</th><th className="py-2 px-2">Cc</th>
                    <th className="py-2 px-2">Objet</th><th className="py-2 px-2">Statut</th>
                  </tr></thead>
                  <tbody>
                    {emailLog.map((m) => (
                      <tr key={m.id} className="border-t border-zinc-900" data-testid={`email-log-${m.id}`}>
                        <td className="py-1.5 px-2 text-zinc-400 whitespace-nowrap">{(m.created_at || "").slice(0, 16).replace("T", " ")}</td>
                        <td className="py-1.5 px-2 text-zinc-200">{(m.to || []).join(", ")}</td>
                        <td className="py-1.5 px-2 text-zinc-500">{(m.cc || []).join(", ") || "—"}</td>
                        <td className="py-1.5 px-2 text-zinc-300">{m.subject}</td>
                        <td className="py-1.5 px-2"><span className={`px-1.5 py-0.5 rounded ${m.status === "sent" ? "bg-emerald-600/30 text-emerald-300" : "bg-red-600/30 text-red-300"}`}>{m.status}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
        </div>
      )}

      {/* RÉSUMÉ ASSUREUR (imprimable) */}
      {tab === "submission" && (
        <div className="space-y-4" data-testid="tab-content-submission">
          <div className="rounded-xl border border-amber-800/60 bg-amber-950/20 p-4 space-y-3" data-testid="send-insurer-box">
            <div className="text-sm font-semibold text-amber-300 flex items-center gap-2"><Send size={16} /> Envoyer le résumé à l'assureur par courriel</div>
            <p className="text-xs text-zinc-400">Le courriel inclut le résumé complet + un PDF officiel du dossier + tous les documents téléversés en pièces jointes. Une copie (cc) sera envoyée à ralph646@hotmail.com et une entrée sera ajoutée aux « Communications assureur ».</p>
            <div className="flex flex-col sm:flex-row gap-2">
              <input type="email" value={insurerEmail} onChange={(e) => setInsurerEmail(e.target.value)}
                placeholder="Courriel Desjardins (ex. reclamations@desjardins.com)"
                className={inp + " sm:flex-1"} data-testid="insurer-email-input" />
              <button onClick={sendToInsurer} disabled={sending} data-testid="send-insurer-btn"
                className="flex items-center justify-center gap-2 bg-emerald-600 text-white font-semibold px-5 py-2.5 rounded-lg hover:bg-emerald-500 disabled:opacity-50 whitespace-nowrap">
                <Send size={16} /> {sending ? "Envoi…" : "Envoyer à Desjardins"}</button>
            </div>
          </div>
          <div className="rounded-xl border border-sky-800/60 bg-sky-950/20 p-4 space-y-3" data-testid="notify-tenant-box">
            <div className="text-sm font-semibold text-sky-300 flex items-center gap-2"><Send size={16} /> Message à l'organisme locataire (Mino / Mélanie Dostie)</div>
            <p className="text-xs text-zinc-400">Génère et envoie un courriel clair expliquant les dégâts, les travaux Qualinet et la démarche d'assurance (dossier n° {claim.claim_number || "—"}). Copie (cc) à ralph646@hotmail.com.</p>
            <div className="flex flex-col sm:flex-row gap-2">
              <input type="email" value={tenantEmail} onChange={(e) => setTenantEmail(e.target.value)}
                placeholder="Courriel de l'organisme locataire (Mélanie Dostie)"
                className={inp + " sm:flex-1"} data-testid="tenant-email-input" />
              <button onClick={notifyTenant} disabled={sendingTenant} data-testid="notify-tenant-btn"
                className="flex items-center justify-center gap-2 bg-sky-600 text-white font-semibold px-5 py-2.5 rounded-lg hover:bg-sky-500 disabled:opacity-50 whitespace-nowrap">
                <Send size={16} /> {sendingTenant ? "Envoi…" : "Envoyer au locataire"}</button>
            </div>
          </div>
          {/* Compositeur — courriel personnalisé à un destinataire au choix (M. Laroche) */}
          <div className="rounded-xl border border-violet-800/60 bg-violet-950/20 p-4 space-y-3" data-testid="custom-letter-box">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div className="text-sm font-semibold text-violet-300 flex items-center gap-2"><Send size={16} /> Composer un courriel (M. Laroche / autre destinataire)</div>
              <button onClick={generateLaroche} disabled={drafting} data-testid="draft-laroche-btn"
                className="flex items-center gap-2 bg-violet-700 text-white text-sm font-semibold px-3 py-1.5 rounded-lg hover:bg-violet-600 disabled:opacity-50">
                <FileText size={15} /> {drafting ? "Génération…" : "Brouillon M. Laroche"}</button>
              <button onClick={generatePreservation} disabled={drafting} data-testid="draft-preservation-btn"
                className="flex items-center gap-2 bg-sky-700 text-white text-sm font-semibold px-3 py-1.5 rounded-lg hover:bg-sky-600 disabled:opacity-50">
                <FileText size={15} /> {drafting ? "Génération…" : "Brouillon préservation (Mino / Câlinet)"}</button>
            </div>
            <p className="text-xs text-zinc-400">Le brouillon M. Laroche est <b>factuel et neutre</b> et n'admet pas que la moisissure soit une situation distincte. <b>Relis-le et modifie-le</b> ci-dessous ; rien n'est envoyé tant que tu ne cliques pas « Envoyer ».</p>
            <div className="flex flex-col sm:flex-row gap-2">
              <input type="email" value={letterTo} onChange={(e) => setLetterTo(e.target.value)}
                placeholder="Destinataire (ex. jean-sebastien.laroche@indemnipro.ca)" className={inp + " sm:flex-1"} data-testid="letter-to-input" />
              <input type="email" value={letterCc} onChange={(e) => setLetterCc(e.target.value)}
                placeholder="Copie (cc)" className={inp + " sm:w-64"} data-testid="letter-cc-input" />
            </div>
            <input type="text" value={letterSubject} onChange={(e) => setLetterSubject(e.target.value)}
              placeholder="Objet du courriel" className={inp} data-testid="letter-subject-input" />
            <textarea value={letterBody} onChange={(e) => setLetterBody(e.target.value)} rows={14}
              placeholder="Corps du courriel — clique « Générer le brouillon » ou écris ton texte ici."
              className={inp + " font-mono text-xs leading-relaxed"} data-testid="letter-body-input" />
            <label className="flex items-center gap-2 text-xs text-zinc-300">
              <input type="checkbox" checked={letterPdf} onChange={(e) => setLetterPdf(e.target.checked)} data-testid="letter-pdf-check" />
              Joindre le PDF officiel du dossier
            </label>
            <label className="flex items-center gap-2 text-xs text-zinc-300">
              <input type="checkbox" checked={letterFollowup} onChange={(e) => setLetterFollowup(e.target.checked)} data-testid="letter-followup-check" />
              Programmer une relance automatique dans 10 jours si pas de réponse
            </label>
            <label className="flex items-center gap-2 text-xs text-zinc-300">
              <input type="checkbox" checked={letterPhotos} onChange={(e) => setLetterPhotos(e.target.checked)} data-testid="letter-photos-check" />
              Joindre les photos annotées du dossier ({(claim.photos || []).length})
            </label>
            <button onClick={sendCustom} disabled={sendingCustom} data-testid="send-custom-btn"
              className="flex items-center justify-center gap-2 bg-emerald-600 text-white font-semibold px-5 py-2.5 rounded-lg hover:bg-emerald-500 disabled:opacity-50">
              <Send size={16} /> {sendingCustom ? "Envoi…" : "Envoyer le courriel"}</button>
          </div>
          <button onClick={() => window.print()} data-testid="print-btn"
            className="flex items-center gap-2 bg-amber-500 text-zinc-950 font-semibold px-4 py-2 rounded-lg hover:bg-amber-400"><Printer size={16} /> Imprimer / PDF</button>
          <button onClick={downloadPdf} data-testid="download-pdf-btn"
            className="flex items-center gap-2 bg-zinc-800 border border-amber-700 text-amber-300 font-semibold px-4 py-2 rounded-lg hover:bg-zinc-700 ml-2"><FileText size={16} /> Générer le PDF officiel</button>
          <div className="rounded-xl border border-zinc-700 p-6 bg-white text-zinc-900 print:shadow-none" data-testid="submission-sheet">
            <h2 className="text-2xl font-bold">Résumé de sinistre – Envoi à l'assureur</h2>
            <p className="text-sm text-zinc-600 mb-4">Généré depuis Kaizen System</p>
            <table className="text-sm mb-4">
              <tbody>
                {[["Propriété", claim.property_address], ["Type de sinistre", claim.sinistre_type], ["Date de découverte", claim.discovered_at], ["Logements touchés", (claim.units || []).join(", ")], ["Assureur (le vôtre)", claim.insurer], ["N° de police", claim.policy_number], ["N° de dossier réclamation", claim.claim_number], ["Expert en sinistre", claim.expert_name], ["Coordonnées expert", claim.expert_contact], ["Assureur responsabilité", claim.liability_insurer], ["Expert responsabilité", claim.liability_adjuster], ["Coord. expert responsabilité", claim.liability_adjuster_contact], ["N° dossier responsabilité", claim.liability_claim_number], ["Franchise", claim.deductible], ["Couverture", claim.coverage_notes], ["Cause supposée", claim.cause]].map(([k, v]) => (
                  <tr key={k}><td className="pr-4 py-0.5 font-semibold align-top">{k}</td><td className="py-0.5">{v || "—"}</td></tr>
                ))}
              </tbody>
            </table>
            <h3 className="font-bold mt-4 mb-1">Travaux d'urgence effectués</h3>
            <ul className="text-sm list-disc pl-5">
              {(claim.vendors || []).map((v) => <li key={v.id}>{v.name} — {v.work} {v.date && `(${v.date})`}</li>)}
              {(claim.vendors || []).length === 0 && <li>—</li>}
            </ul>
            <h3 className="font-bold mt-4 mb-1">Documents fournis</h3>
            <ul className="text-sm list-disc pl-5">
              {(claim.documents || []).map((d) => <li key={d.id}>{d.category} — {d.filename}</li>)}
              {(claim.documents || []).length === 0 && <li>—</li>}
            </ul>
            <h3 className="font-bold mt-4 mb-1">Questions à confirmer avec l'assureur</h3>
            <ol className="text-sm list-decimal pl-5">
              {(claim.insurer_questions || []).map((q, i) => <li key={i}>{q}</li>)}
            </ol>
          </div>
        </div>
      )}
    </div>
  );
}
